import { format } from "date-fns";
import { useState } from "react";

import {
  CalendarDateRange,
  CalendarPicker,
  SearchableDropdown,
  SearchableDropdownItem,
} from "@fronterahealth/frontera-ui-components";

import { useRbtSessionVideosAnalyticsQuery } from "@api/graphql/types-and-hooks";
import { CommentsAndFeedback } from "@pages/VideoAnalytics/RBTs/CommentsAndFeedback";
import { GraphDialogs } from "@pages/VideoAnalytics/RBTs/GraphDialogs";
import { RecordingMetrics } from "@pages/VideoAnalytics/RBTs/RecordingMetrics";
import { DATE_FORMAT } from "@pages/VideoAnalytics/helpers";
import { useVideoAnalytics } from "@providers/VideoAnalyticsProvider";

export const RBTMetrics = () => {
  const { isAllRBTFetching, allRBTList, selectedRBTs, setSelectedRBTs, selectedRBTDates, setSelectedRBTDates } =
    useVideoAnalytics();
  const [recentSearchesForRBT, setRecentSearchesForRBT] = useState<SearchableDropdownItem[]>([]);

  const [isVideosRecordedDialogOpen, setIsVideosRecordedDialogOpen] = useState<boolean>(false);
  const [isVideoRecordingDurationDialogOpen, setIsVideoRecordingDurationDialogOpen] = useState<boolean>(false);
  const [isAvgVideoUploadTimeDialogOpen, setIsAvgVideoUploadTimeDialogOpen] = useState<boolean>(false);

  const [isRBTResToBCBAQueDialogOpen, setIsRBTResToBCBAQueDialogOpen] = useState<boolean>(false);
  const [isRBTResRateToBCBAQuesDialogOpen, setIsRBTResRateToBCBAQuesDialogOpen] = useState<boolean>(false);

  const [isCommentDistributionOpen, setIsCommentDistributionOpen] = useState<boolean>(false);
  const [isCommentOpen, setIsCommentOpen] = useState<boolean>(false);

  const [isAvgQuestionAsked, setIsAvgQuestionAsked] = useState<boolean>(false);

  const handleDateChange = (newDates: CalendarDateRange) => {
    setSelectedRBTDates(newDates);
  };

  const queryKey = [
    "rbtSessionVideosAnalytics",
    selectedRBTDates?.startDate?.toISOString() || "",
    selectedRBTDates?.endDate?.toISOString() || "",
    selectedRBTDates?.comparisonStartDate?.toISOString() || "",
    selectedRBTDates?.comparisonEndDate?.toISOString() || "",
    selectedRBTs.map((item) => item.id).join(","),
  ];

  const { data: RBTAnalyticsData, isFetching } = useRbtSessionVideosAnalyticsQuery(
    {
      fromDate: selectedRBTDates?.startDate
        ? format(selectedRBTDates.startDate, DATE_FORMAT)
        : format(new Date(), DATE_FORMAT),
      toDate: selectedRBTDates?.endDate
        ? format(selectedRBTDates.endDate, DATE_FORMAT)
        : format(new Date(), DATE_FORMAT),
      comparisonFromDate: selectedRBTDates?.comparisonStartDate
        ? format(selectedRBTDates.comparisonStartDate, DATE_FORMAT)
        : undefined,
      comparisonToDate: selectedRBTDates?.comparisonEndDate
        ? format(selectedRBTDates.comparisonEndDate, DATE_FORMAT)
        : undefined,
      rbtIds: selectedRBTs.map((item) => item.id),
    },
    {
      queryKey,
      refetchOnWindowFocus: false,
      retry: false,
      enabled:
        Boolean(selectedRBTDates?.startDate && selectedRBTDates?.endDate) ||
        Boolean(selectedRBTDates?.comparisonStartDate && selectedRBTDates?.comparisonEndDate),
    },
  );

  const currentData = RBTAnalyticsData?.rbtSessionVideosAnalytics?.currentData;
  const previousPeriodData = RBTAnalyticsData?.rbtSessionVideosAnalytics?.comparisonData;

  const handleRecentSearchAddForRBT = (item: SearchableDropdownItem) => {
    setRecentSearchesForRBT((prev) => [...prev, item].slice(-5)); // Keep the last 5 searches
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-row items-center gap-2 justify-start">
        <SearchableDropdown
          items={allRBTList}
          label="RBTs"
          placeholder="All RBTs"
          onSelectionChange={(items: SearchableDropdownItem[]) => {
            setSelectedRBTs(items);
          }}
          recentSearches={recentSearchesForRBT}
          onRecentSearchAdd={handleRecentSearchAddForRBT}
          isLoading={isAllRBTFetching}
          selectedItems={selectedRBTs}
          disabled={isAllRBTFetching}
        />
        <CalendarPicker
          comparisonEnabled
          placeholder="Date"
          selectedDateRange={selectedRBTDates}
          inputSize="sm"
          onDateChange={handleDateChange}
          mode="filter"
          selectedLabel={"Last 7 Days"}
          selectedComparisonLabel="Previous Period"
        />
      </div>
      <RecordingMetrics
        currentData={currentData}
        previousPeriodData={previousPeriodData}
        isFetching={isFetching}
        setIsVideosRecordedDialogOpen={setIsVideosRecordedDialogOpen}
        setIsAvgVideoUploadTimeDialogOpen={setIsAvgVideoUploadTimeDialogOpen}
        setIsVideoRecordingDurationDialogOpen={setIsVideoRecordingDurationDialogOpen}
      />
      <CommentsAndFeedback
        currentData={currentData}
        previousPeriodData={previousPeriodData}
        isFetching={isFetching}
        setIsRBTResToBCBAQueDialogOpen={setIsRBTResToBCBAQueDialogOpen}
        setIsRBTResRateToBCBAQuesDialogOpen={setIsRBTResRateToBCBAQuesDialogOpen}
        setIsCommentOpen={setIsCommentOpen}
        setIsCommentDistributionOpen={setIsCommentDistributionOpen}
        setIsAvgQuestionAsked={setIsAvgQuestionAsked}
      />
      <GraphDialogs
        isVideosRecordedDialogOpen={isVideosRecordedDialogOpen}
        setIsVideosRecordedDialogOpen={setIsVideosRecordedDialogOpen}
        isVideoRecordingDurationDialogOpen={isVideoRecordingDurationDialogOpen}
        setIsVideoRecordingDurationDialogOpen={setIsVideoRecordingDurationDialogOpen}
        isAvgVideoUploadTimeDialogOpen={isAvgVideoUploadTimeDialogOpen}
        setIsAvgVideoUploadTimeDialogOpen={setIsAvgVideoUploadTimeDialogOpen}
        isRBTResToBCBAQueDialogOpen={isRBTResToBCBAQueDialogOpen}
        setIsRBTResToBCBAQueDialogOpen={setIsRBTResToBCBAQueDialogOpen}
        isRBTResRateToBCBAQuesDialogOpen={isRBTResRateToBCBAQuesDialogOpen}
        setIsRBTResRateToBCBAQuesDialogOpen={setIsRBTResRateToBCBAQuesDialogOpen}
        isCommentOpen={isCommentOpen}
        setIsCommentOpen={setIsCommentOpen}
        isCommentDistributionOpen={isCommentDistributionOpen}
        setIsCommentDistributionOpen={setIsCommentDistributionOpen}
        isAvgQuestionAsked={isAvgQuestionAsked}
        setIsAvgQuestionAsked={setIsAvgQuestionAsked}
      />
    </div>
  );
};
